import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Route, withRouter } from 'react-router-dom';
import moment from 'moment-timezone';

import fetchSalon from 'actions/fetchSalon.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import { LT_ROUTE } from 'bl/loggers.bl';
import SENTRY from 'config/sentry.singleton';
import TOKEN_REFRESHER_TIMER from 'config/trt.singleton';
import { selectCurrentSalon, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { refreshCognitoUserToken, selectCognito } from 'utils/cognito.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import PrivateRoute from 'lib/PrivateRoute';

const CTAG = 'SalonLoader';
const L = logger([CTAG, LT_ROUTE]);

const USE_UPDATED_MOMENT_LOCALE = false;

const LoadedSalonRoute = (props) => {
  const { salonId } = props;

  // SEE: https://getslick.atlassian.net/browse/GS-13377
  // SEE: https://www.npmjs.com/package/react-datepicker/v/1.8.0
  // SEE: https://momentjs.com/docs/#/customization/dow-doy/
  if (USE_UPDATED_MOMENT_LOCALE) moment.locale('en-GB');

  // use memo - memo executes right away, effect only after render
  useMemo(() => void SENTRY.salon({ salonId, tag: CTAG }), [salonId]);

  return <Route {...props} />;
};

class SalonLoader extends React.Component {
  constructor(props) {
    super(props);

    const intervalId = setInterval(() => void this.refetchSalonDetails(), 1000);
    this.state = { intervalId };
  }

  componentDidMount() {
    if (!this.props.currentSalonId) this.refetchSalonDetails();
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { match, fetchSalon, cognito, getStylists } = this.props;

    const dispatch = nextProps?.dispatch;

    const id = match?.params?.id;
    const prevUser = cognito?.user;
    const nextUser = nextProps?.cognito?.user;

    const prevToken = prevUser?.signInUserSession?.refreshToken?.token;
    const nextToken = nextUser?.signInUserSession?.refreshToken?.token;

    if (nextUser && prevUser && dispatch && nextToken !== prevToken) this.setRefreshTimer(nextProps);

    const currentSalonId = nextProps?.currentSalonId;
    const newSalonId = parseInt(id, 10);

    if (currentSalonId && currentSalonId !== newSalonId) {
      L.unignore('componentWillReceiveProps()', 'fetchSalon:', fetchSalon(newSalonId));
      L.unignore('componentWillReceiveProps()', 'getStylists:', getStylists(newSalonId));
    }
  }

  setRefreshTimer = (nextProps) => {
    const { cognito } = nextProps;
    const { userPool, user } = asObject(cognito);
    const { username, signInUserSession } = asObject(user);

    if (!user || !signInUserSession) return;

    // We've got a new refresh token!

    const { refreshToken, idToken } = signInUserSession;
    const beforeExpiryTime = idToken.getExpiration() - 5 * 60;

    let timeToRefresh = beforeExpiryTime * 1000 - new Date();
    if (timeToRefresh < 0) timeToRefresh = 0;

    clearTimeout(TOKEN_REFRESHER_TIMER.get());
    TOKEN_REFRESHER_TIMER.set(
      setTimeout(
        () =>
          refreshCognitoUserToken({
            userPool,
            userName: username,
            refreshToken: refreshToken.getToken(),
            dispatch: nextProps.dispatch,
          }),
        timeToRefresh,
      ),
    );
  };

  refetchSalonDetails = () => {
    const { intervalId } = this.state;
    const { fetchSalon, currentSalonId, currentSalon } = this.props;

    const id = this.props?.match?.params?.id;
    if (id === `${currentSalonId}`) {
      clearInterval(intervalId);
      return;
    }

    if (currentSalon?.isFetching || currentSalon?.error) return;

    L.unignore('refetchSalonDetails()', 'fetchSalon:', fetchSalon(parseInt(id, 10)));
  };

  render() {
    const props = asObject(this.props);
    const { match, currentSalonId, currentSalon } = this.props;

    if (match.params.id === `${currentSalonId}`) {
      return <LoadedSalonRoute {...props} salonId={currentSalonId} />;
    }

    const { isFetching, error } = currentSalon ?? {};

    return (
      <div data-bem="SalonLoader" className="loading-wrap">
        <div data-bem="SalonLoader__animation" className="loading-anim">
          <div data-bem="SalonLoader__title" className="loading-title">
            <span data-bem="SalonLoader__text">Loading Salon Data...</span>
          </div>
        </div>

        {fromAnyToBool(error) && (
          <div data-bem="SalonLoader__error" className="loading-feedback">{`${error?.code}: ${error?.message}`}</div>
        )}

        {fromAnyToBool(isFetching) && (
          <div data-bem="SalonLoader__fetch" className="loading-feedback">
            Checking Salon Details
          </div>
        )}

        {fromAnyToBool(!isFetching && !error) && (
          <div data-bem="SalonLoader__normal" className="loading-feedback">
            Twiddling digital thumbs
          </div>
        )}
      </div>
    );
  }
}

const SalonLoaderHoc = withRouter(
  connect(
    (state) => ({
      currentSalonId: selectCurrentSalonId(state),
      currentSalon: selectCurrentSalon(state),
      cognito: selectCognito(state),
      dispatch: state?.dispatch,
    }),
    mapActionsToDispatchToProps({
      fetchSalon,
      getStylists: fetchStylistsBySalon,
    }),
  )(SalonLoader),
);

const SalonRoute = ({ component, ...rest }) => (
  <PrivateRoute {...rest} component={SalonLoaderHoc} componentToLoad={component} />
);

export default SalonRoute;
